import {
  TILAAJAVASTUU_FOLDER_ID_PREFIX,
  getTilaajavastuuFolderId,
} from './bankSidebarCategories';
import { isProjectBankFormsFolderId } from './projectBankForms';

/**
 * Where the bank is, as the URL carries it:
 *  - `public`        a folder of the public bank: `?folder=<FilePermission id>`
 *                    (virtual forms folder ids included)
 *  - `tilaajavastuu` a project's contractor-liability documents view:
 *                    `?folder=tilaajavastuu-folder_<projectId>[&business=<id>]`
 *  - `private`       the private bank: `?mode=private[&folder=<item id>]`,
 *                    no folder meaning the private root
 * `?path=<FilePermission.path>` is the older form the dashboard buttons and
 * the assistant's links still use; it is read, never written.
 */
export const BANK_LOCATION_KINDS = {
  PUBLIC: 'public',
  PRIVATE: 'private',
  TILAAJAVASTUU: 'tilaajavastuu',
};

export const BANK_LOCATION_PARAMS = {
  FOLDER: 'folder',
  MODE: 'mode',
  BUSINESS: 'business',
  PATH: 'path',
};

const PRIVATE_MODE = 'private';

/**
 * The bank opened as the project's gallery (`?view=gallery`, from the
 * dashboards' Galleria button): its images folder alone, without the
 * sidebar, newest first. Not one of BANK_LOCATION_PARAMS, so the URL sync
 * keeps it while the user moves about, a reload included.
 */
export const BANK_VIEW_PARAM = 'view';
export const BANK_GALLERY_VIEW = 'gallery';

/**
 * Whether a query string opens the bank as the project's gallery.
 * @param {string} search location.search, with or without the '?'
 * @returns {boolean}
 */
export const isBankGalleryViewSearch = (search) =>
  new URLSearchParams(search || '').get(BANK_VIEW_PARAM) === BANK_GALLERY_VIEW;

/**
 * A query string with `?view=gallery` put back where the bank is the
 * project's gallery and a link in the app (the app bar's crumb, say) left
 * it out; any other query string as it was.
 * @param {string} search '' or '?…'
 * @param {boolean} isGalleryView
 * @returns {string} '' or '?…'
 */
export const withBankGalleryView = (search, isGalleryView) => {
  if (!isGalleryView || isBankGalleryViewSearch(search)) return search || '';
  const params = new URLSearchParams(search || '');
  params.set(BANK_VIEW_PARAM, BANK_GALLERY_VIEW);
  return `?${params.toString()}`;
};

/**
 * Project id of a tilaajavastuu pseudo-folder id, or null for any other id.
 * @param {string} folderId
 * @returns {string|null}
 */
export const getTilaajavastuuProjectIdFromFolderId = (folderId) =>
  typeof folderId === 'string' &&
  folderId.startsWith(TILAAJAVASTUU_FOLDER_ID_PREFIX)
    ? folderId.slice(TILAAJAVASTUU_FOLDER_ID_PREFIX.length) || null
    : null;

/**
 * What a query string asks for, before any lookup: every field null when it
 * is not there. `isEmpty` is true for a URL that names nothing (the project
 * root is meant).
 * @param {string} search location.search, with or without the '?'
 * @returns {{folderId: string|null, path: string|null, isPrivate: boolean, businessId: string|null, isEmpty: boolean}}
 */
export const parseBankLocationQuery = (search) => {
  const params = new URLSearchParams(search || '');
  const folderId = params.get(BANK_LOCATION_PARAMS.FOLDER) || null;
  const path = params.get(BANK_LOCATION_PARAMS.PATH) || null;
  const isPrivate =
    (params.get(BANK_LOCATION_PARAMS.MODE) || '').toLowerCase() ===
    PRIVATE_MODE;
  const businessId = params.get(BANK_LOCATION_PARAMS.BUSINESS) || null;

  return {
    folderId,
    path,
    isPrivate,
    businessId,
    isEmpty: !folderId && !path && !isPrivate && !businessId,
  };
};

/**
 * Whether the query asks for something that needs the project's context —
 * its entity and the user's permissions — before it can be opened: the
 * private bank and the tilaajavastuu view are for permitted users only, and
 * a forms folder is built from the project entity. Told from the query
 * alone, without any lookup.
 * @param {ReturnType<typeof parseBankLocationQuery>} query
 * @returns {boolean}
 */
export const queryNeedsProjectContext = (query) =>
  Boolean(
    query &&
    (query.isPrivate ||
      getTilaajavastuuProjectIdFromFolderId(query.folderId) !== null ||
      isProjectBankFormsFolderId(query.folderId || ''))
  );

/**
 * The query string for a location, with any params that are not the bank's
 * kept as they were; '' for no params at all.
 * @param {{kind: string, folderId?: string, projectId?: string, businessId?: string}|null} location
 * @param {string} [search] the current location.search whose other params are kept
 * @returns {string} '' or '?…'
 */
export const serialiseBankLocation = (location, search = '') => {
  const params = new URLSearchParams(search || '');
  Object.values(BANK_LOCATION_PARAMS).forEach((key) => params.delete(key));

  if (location) {
    if (location.kind === BANK_LOCATION_KINDS.PRIVATE) {
      params.set(BANK_LOCATION_PARAMS.MODE, PRIVATE_MODE);
      if (location.folderId) {
        params.set(BANK_LOCATION_PARAMS.FOLDER, location.folderId);
      }
    } else if (location.kind === BANK_LOCATION_KINDS.TILAAJAVASTUU) {
      params.set(
        BANK_LOCATION_PARAMS.FOLDER,
        getTilaajavastuuFolderId(location.projectId)
      );
      if (location.businessId) {
        params.set(BANK_LOCATION_PARAMS.BUSINESS, location.businessId);
      }
    } else if (location.kind === BANK_LOCATION_KINDS.PUBLIC) {
      if (location.folderId) {
        params.set(BANK_LOCATION_PARAMS.FOLDER, location.folderId);
      }
    }
  }

  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
};

/**
 * Whether two locations name the same place; null and undefined are the
 * same "nowhere", a missing business or private folder the same as ''.
 * @param {Object|null} a
 * @param {Object|null} b
 * @returns {boolean}
 */
export const isSameBankLocation = (a, b) => {
  if (!a || !b) return !a && !b;
  if (a.kind !== b.kind) return false;

  switch (a.kind) {
    case BANK_LOCATION_KINDS.PRIVATE:
      return (a.folderId || '') === (b.folderId || '');
    case BANK_LOCATION_KINDS.TILAAJAVASTUU:
      return (
        a.projectId === b.projectId &&
        (a.businessId || '') === (b.businessId || '')
      );
    case BANK_LOCATION_KINDS.PUBLIC:
      return a.folderId === b.folderId;
    default:
      return false;
  }
};
